import { kebabCase } from "@/Utils/formatters";
import { appstr } from "@coreapp/utils/appstr";
import {
  BigInfoIcon,
  Button,
  DataTable,
  Flex,
  Icon,
  Menu,
  Search,
  Text
} from "@theorgchartdev/storybook";
import { useMemo, useState } from "react";
import FieldSelector from "../ImportFiltersConfiguration/FieldSelector";
import {
  enumLanguage,
  enumObjClassNames,
  enumObjectTypes,
  kExcludedLimitFieldsEmployeeCharts,
  kExcludedLimitFieldsPositionCharts,
  languageNames
} from "@coreapp/constants";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import SectionCard from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { createColumnWithMeta } from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import FieldTypePill from "../FieldsFormulasConfiguration/FieldTypePill";
import { MAPPING_TEST_PREFIX } from "./Utils";

type LocalizationData = {
  chartConfig: any;
  onUpdate: (config: any, fieldChanged: string) => void;
};

type Language = (typeof enumLanguage)[keyof typeof enumLanguage];

type LanguageTableData = {
  fieldKey: string;
  name: string;
  type: (typeof enumObjectTypes)[keyof typeof enumObjectTypes];
  languages: { [language in Language]?: string };
};

type LocalizationConfig = {
  supportedLanguages: Language[];
  fields: {
    [fieldKey: string]: {
      languages: { [language in Language]?: string };
      name: string;
      type: string;
    };
  };
};

const languageOptionsOrder = [
  enumLanguage.english,
  enumLanguage.spanish,
  enumLanguage.french,
  enumLanguage.german
];

const ensureLocalizationConfig = (config: any): LocalizationConfig => {
  const lc = config.localizationConfig;
  if (lc?.supportedLanguages && lc?.fields) return JSON.parse(JSON.stringify(lc));
  return { supportedLanguages: [], fields: {} };
};

export const localizationPreSave = (chartSettings: any): any => {
  const lc: LocalizationConfig = JSON.parse(JSON.stringify(chartSettings.localizationConfig || {}));
  const fields = lc.fields ?? {};
  const supportedLanguages = lc.supportedLanguages ?? [];

  // Remove fields with no language mappings
  Object.keys(fields).forEach(fieldKey => {
    if (Object.keys(fields[fieldKey]?.languages ?? {}).length === 0) {
      delete fields[fieldKey];
    }
  });

  // Remove languages with no mapped fields
  for (let i = supportedLanguages.length - 1; i >= 0; i--) {
    const lang = supportedLanguages[i];
    const hasField = Object.values(fields).some((field: any) => field?.languages?.[lang]);
    if (!hasField) supportedLanguages.splice(i, 1);
  }

  return { ...chartSettings, localizationConfig: lc };
};

type FieldNameCellProps = {
  data: LanguageTableData;
  onDelete: () => void;
  isPositionChart: boolean;
};

const FieldNameCell = ({ data, onDelete, isPositionChart }: FieldNameCellProps) => (
  <ActionListItem
    label={data.name}
    contentAfter={
      isPositionChart ? (
        <FieldTypePill
          type={data.type === enumObjectTypes.holder ? "employee" : "position"}
          pillProps={{ size: "regular" }}
        />
      ) : null
    }
    actions={[
      {
        iconName: "trash-can",
        iconProps: { color: "error" },
        onClick: onDelete,
        dataTestId: `${MAPPING_TEST_PREFIX}-loc-delete-field-btn-${data.fieldKey}`
      }
    ]}
    innerFlexProps={{
      flex: 0
    }}
    flexProps={{
      gap: "8px"
    }}
  />
);

type FieldMappingCellProps = {
  data: LanguageTableData;
  language: number;
  chartConfig: any;
  onSelect: (fieldName: string) => void;
  hiddenFields: string[];
};

const FieldMappingCell = ({
  data,
  language,
  chartConfig,
  onSelect,
  hiddenFields
}: FieldMappingCellProps) => (
  <div style={{ width: "100%" }}>
    <FieldSelector
      chartConfig={chartConfig}
      onClick={(_option: any, value: any) => onSelect(value.fieldName)}
      fieldName={data.languages?.[language] ?? ""}
      fieldType={data.type}
      onlyFromType={data.type}
      containerProps={{
        dataTestId: `${MAPPING_TEST_PREFIX}-loc-field-mapping`,
        selectSize: "medium",
        listProps: {
          enableSearch: true,
          inputProps: {
            wrapperDivClass: "oc-heading-field-input-search"
          }
        }
      }}
      filterFields={hiddenFields}
      includePills={false}
    />
  </div>
);

const AddMenuTrigger = ({
  label,
  disabled,
  dataTestId
}: {
  label: string;
  disabled?: boolean;
  dataTestId?: string;
}) => (
  <Button
    dataTestId={dataTestId}
    iconProps={{ size: "xsmall", name: "chevron-down" }}
    modifier="secondary"
    iconAlignEnd
    disabled={disabled}
  >
    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Icon name="plus" size="small" disabled={disabled} />
      {label}
    </span>
  </Button>
);

type DeleteLanguageButtonProps = {
  onConfirmed: () => void;
};

const DeleteLanguageButton = ({
  onConfirmed,
  language
}: DeleteLanguageButtonProps & { language: number }) => (
  <Button
    dataTestId={`${MAPPING_TEST_PREFIX}-loc-delete-language-btn-${kebabCase(languageNames[language])}`}
    iconProps={{ name: "trash-can", color: "error" }}
    size="small"
    modifier="text"
    onClick={() => {
      GetWSMgr()?.CreateReactAlert(
        appstr("Delete language"),
        appstr(
          "Are you sure you want to delete this language? All mapped field configurations for this language will be lost."
        ),
        onConfirmed,
        () => {},
        appstr("Delete"),
        appstr("Cancel"),
        () => {}
      );
    }}
  />
);

function buildNameColumn(
  chartConfig: any,
  onDeleteField: (fieldKey: string) => void
): ColumnDef<LanguageTableData> {
  return createColumnWithMeta(
    {
      id: "name",
      accessorKey: "name",
      header: appstr("Field name"),
      enableColumnFilter: false,
      size: 250,
      cell: ({ row }) => {
        const data = row.original;
        return (
          <FieldNameCell
            data={data}
            onDelete={() => onDeleteField(data.fieldKey)}
            isPositionChart={IsPositionChart(chartConfig)}
          />
        );
      }
    },
    { isDraggable: false }
  );
}

function buildLanguageColumn(
  language: number,
  chartConfig: any,
  onUpdateFieldMapping: (fieldKey: string, language: number, fieldName: string) => void,
  onDeleteLanguage: (language: number) => void,
  hiddenFields: string[]
): ColumnDef<LanguageTableData, any> {
  return createColumnWithMeta(
    {
      id: `lang_${language}`,
      accessorFn: (row: LanguageTableData) => row.languages?.[language] ?? "",
      enableColumnFilter: false,
      size: 300,
      maxSize: 400,
      header: appstr(languageNames[language]),
      cell: ({ row }: { row: Row<LanguageTableData> }) => {
        const data = row.original;
        return (
          <FieldMappingCell
            data={data}
            language={language}
            chartConfig={chartConfig}
            onSelect={fieldName => onUpdateFieldMapping(data.fieldKey, language, fieldName)}
            hiddenFields={hiddenFields}
          />
        );
      }
    },
    {
      isDraggable: false,
      headerAction: (
        <DeleteLanguageButton language={language} onConfirmed={() => onDeleteLanguage(language)} />
      )
    }
  );
}

export const LocalizationCard = (props: LocalizationData) => {
  const [searchTerm, setSearchTerm] = useState("");
  const localizationConfig: LocalizationConfig = ensureLocalizationConfig(props.chartConfig);

  const onUpdateLocalization = (localizationConfig: LocalizationConfig) => {
    props.onUpdate({ localizationConfig }, "localizationConfig");
  };

  const deleteField = (fieldKey: string) => {
    const newConfig = { ...props.chartConfig };
    const lc = ensureLocalizationConfig(newConfig);
    delete lc.fields[fieldKey];
    onUpdateLocalization(lc);
  };

  const updateFieldMapping = (fieldKey: string, language: number, fieldName: string) => {
    const newConfig = { ...props.chartConfig };
    const lc = ensureLocalizationConfig(newConfig);
    lc.fields[fieldKey].languages[language] = fieldName;
    onUpdateLocalization(lc);
  };

  const deleteLanguage = (language: number) => {
    const newConfig = { ...props.chartConfig };
    const lc = ensureLocalizationConfig(newConfig);
    lc.supportedLanguages = lc.supportedLanguages.filter((l: number) => l !== language);
    Object.keys(lc.fields).forEach(fieldKey => {
      delete lc.fields[fieldKey].languages[language];
    });
    onUpdateLocalization(lc);
  };

  const addLanguage = (language: number) => {
    const newConfig = { ...props.chartConfig };
    const lc = ensureLocalizationConfig(newConfig);
    if (!lc.supportedLanguages.includes(language)) lc.supportedLanguages.push(language);
    onUpdateLocalization(lc);
  };

  const addField = (fieldOption: any) => {
    const fieldName = fieldOption.value;
    const dashIdx = fieldName.indexOf("-");
    const className = fieldName.substring(0, dashIdx);
    const field = fieldName.substring(dashIdx + 1);

    const fieldType =
      className === enumObjClassNames.box ? enumObjectTypes.holder : enumObjectTypes.position;
    const newConfig = { ...props.chartConfig };
    const lc = ensureLocalizationConfig(newConfig);
    lc.fields[fieldName] = { name: field, type: fieldType, languages: {} };
    onUpdateLocalization(lc);
  };

  const hiddenFields = useMemo(() => {
    const hiddenFields = new Set<string>();
    const hiddenSystemFields = IsPositionChart(props.chartConfig)
      ? kExcludedLimitFieldsPositionCharts
      : kExcludedLimitFieldsEmployeeCharts;

    const fieldList: string[] = [];
    hiddenSystemFields[enumObjectTypes.holder].forEach((field: string) => {
      fieldList.push(`${enumObjClassNames.box}-${field}`);
    });
    hiddenSystemFields[enumObjectTypes.position].forEach((field: string) => {
      fieldList.push(`${enumObjClassNames.position}-${field}`);
    });

    fieldList.forEach((field: string) => {
      hiddenFields.add(field);
    });

    return [...hiddenFields];
  }, [props.chartConfig]);

  const columns = useMemo((): ColumnDef<LanguageTableData, any>[] => {
    const supportedLanguages = localizationConfig.supportedLanguages ?? [];
    const columnDefinitions: ColumnDef<LanguageTableData, any>[] = [
      buildNameColumn(props.chartConfig, deleteField),
      ...supportedLanguages.map((lang: Language) =>
        buildLanguageColumn(
          lang,
          props.chartConfig,
          updateFieldMapping,
          deleteLanguage,
          hiddenFields
        )
      )
    ];

    // Pad with empty phantom columns until we have at least 3 total (name + 2 language)
    if (columnDefinitions.length < 3) {
      const existingLangs = new Set(supportedLanguages);
      for (const lang of languageOptionsOrder) {
        if (columnDefinitions.length >= 3) break;
        if (!existingLangs.has(lang)) {
          columnDefinitions.push(
            createColumnWithMeta<LanguageTableData, string>(
              {
                id: `phantom_${lang}`,
                accessorFn: (): string => "",
                enableSorting: false,
                enableColumnFilter: false,
                header: "",
                cell: () => <div style={{ width: "100%", height: "32px" }} />
              },
              { isDraggable: false }
            )
          );
        }
      }
    }

    return columnDefinitions;
  }, [props.chartConfig]);

  const tableData: LanguageTableData[] = useMemo(() => {
    if (!localizationConfig) return [];
    return Object.keys(localizationConfig.fields)
      .map(fieldKey => {
        const field = localizationConfig.fields[fieldKey];
        if (searchTerm) {
          const matchesName = field.name.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesValue = Object.values(field.languages).some((v: any) =>
            v.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (!matchesName && !matchesValue) return null;
        }
        return { fieldKey, ...field };
      })
      .filter((item): item is LanguageTableData => Boolean(item));
  }, [props.chartConfig, searchTerm]);

  const languageMenuOptions = languageOptionsOrder
    .filter(v => !localizationConfig?.supportedLanguages?.includes(v))
    .map(v => ({
      label: appstr(languageNames[v]),
      value: v,
      clickHandler: () => addLanguage(v)
    }));

  const cssClass = "ocn-chart-settings-localization-settings";
  const isEmpty = tableData.length === 0 && localizationConfig.supportedLanguages.length === 0;

  return (
    <SectionCard title={appstr("Data localization")} customSectionCardClass={cssClass}>
      <Flex className={`${cssClass}-description`}>
        <Text>{appstr("LocalizationSettingsDesc")}</Text>
      </Flex>

      {/* Toolbar */}
      <Flex justifyContent="space-between">
        <Flex flex={2} gap="12px">
          <FieldSelector
            chartConfig={props.chartConfig}
            onClick={addField}
            useMenu
            containerProps={{
              label: appstr("Add field"),
              selectSize: "medium",
              trigger: (
                <AddMenuTrigger
                  label={appstr("Add field")}
                  dataTestId={`${MAPPING_TEST_PREFIX}-loc-add-field-btn`}
                />
              ),
              triggerProps: { modifier: "secondary" },
              listProps: {
                enableSearch: true,
                inputProps: {
                  wrapperDivClass: "oc-heading-field-input-search"
                }
              }
            }}
            filterFields={[...hiddenFields, ...Object.keys(localizationConfig?.fields || {})]}
            includePills={false}
          />
          <Menu
            dataTestId={`${MAPPING_TEST_PREFIX}-loc-add-language`}
            position="bottom-end"
            options={languageMenuOptions}
            label=""
            listWidth="fill"
            trigger={
              <AddMenuTrigger
                label={appstr("Add language")}
                disabled={languageMenuOptions.length === 0}
                dataTestId={`${MAPPING_TEST_PREFIX}-loc-add-language-btn`}
              />
            }
            triggerProps={{ modifier: "secondary" }}
            disabled={languageMenuOptions.length === 0}
          />
        </Flex>

        <Flex flex={1} justifyContent="flex-end">
          <Search
            dataTestId={`${MAPPING_TEST_PREFIX}-loc-search`}
            onInput={value => setSearchTerm(value)}
            placeholder={appstr("Search for field")}
            wrapperDivClass={`${cssClass}-search-wrapper`}
          />
        </Flex>
      </Flex>

      {/* Body */}
      <Flex className={`${cssClass}-main-body-content`}>
        {isEmpty ? (
          <BigInfoIcon name="language" message={appstr("NoLocalizationData")} color="brand" />
        ) : (
          <div style={{ overflowX: "scroll", maxWidth: "100%" }} className="ocn-list-scrollbar">
            <DataTable.Provider
              data={tableData}
              columns={columns}
              enableColumnFilters={false}
              emptyMessage={appstr("nomatches")}
            >
              <DataTable.Root>
                <DataTable.Header />
                <DataTable.Body />
              </DataTable.Root>
            </DataTable.Provider>
          </div>
        )}
      </Flex>
    </SectionCard>
  );
};
